import React, { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import Icon from "@/components/Icon";
import AdminLayout from "@/components/AdminLayout";

const money = (n) => "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AdminBilling() {
  const [ov, setOv] = useState(null);
  const [tx, setTx] = useState(null);
  useEffect(() => {
    api.get("/admin/billing/overview").then(({ data }) => setOv(data)).catch(() => {});
    api.get("/admin/billing/transactions", { params: { page: 1, page_size: 25 } }).then(({ data }) => setTx(data)).catch(() => {});
  }, []);

  return (
    <AdminLayout title="Billing" subtitle="Subscriptions, revenue and payment activity — Stripe is the source of truth"
      actions={<a data-testid="billing-export" href={`${API}/admin/billing/export`} className="rounded-xl border border-border px-3 py-2 text-sm font-semibold hover:bg-accent">Export CSV</a>}>
      {!ov ? (
        <div className="grid place-items-center py-20"><Icon name="Loader2" className="h-7 w-7 animate-spin text-primary" /></div>
      ) : (
        <div className="space-y-6" data-testid="admin-billing">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card label="MRR" value={money(ov.mrr)} testid="mrr" />
            <Card label="ARR" value={money(ov.arr)} testid="arr" />
            <Card label="Trial conversion" value={`${ov.trial_conversion}%`} testid="conversion" />
            <Card label="Churn" value={`${ov.churn}%`} testid="churn" />
            <Card label="Active" value={ov.subscriptions.active} />
            <Card label="Trialing" value={ov.subscriptions.trial} />
            <Card label="Past due" value={ov.subscriptions.past_due} />
            <Card label="Canceled" value={ov.subscriptions.canceled} />
          </div>

          <div className="rounded-2xl border border-border bg-card p-5">
            <h3 className="text-sm font-bold mb-3">Revenue by plan (MRR)</h3>
            {Object.keys(ov.revenue_by_plan || {}).length === 0 ? <p className="text-sm text-muted-foreground">No active paid subscriptions yet.</p> : (
              <div className="space-y-2">{Object.entries(ov.revenue_by_plan).map(([p, v]) => (
                <div key={p} className="flex items-center justify-between text-sm"><span className="capitalize font-medium">{p}</span><span className="font-mono">{money(v)}</span></div>
              ))}</div>
            )}
          </div>

          <div className="rounded-2xl border border-border bg-card overflow-hidden">
            <div className="px-4 py-3 border-b border-border text-sm font-bold">Recent transactions</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-muted-foreground"><tr>{["Date", "Plan", "Amount", "Status"].map((h) => <th key={h} className="text-left font-semibold px-4 py-2.5">{h}</th>)}</tr></thead>
                <tbody>
                  {!tx && <tr><td colSpan={4} className="px-4 py-8 text-center"><Icon name="Loader2" className="h-5 w-5 animate-spin text-primary mx-auto" /></td></tr>}
                  {tx && tx.items.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">No transactions yet.</td></tr>}
                  {tx && tx.items.map((t, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-4 py-2.5 text-muted-foreground">{t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}</td>
                      <td className="px-4 py-2.5 capitalize">{t.plan}</td>
                      <td className="px-4 py-2.5">{money(t.amount)} {(t.currency || "").toUpperCase()}</td>
                      <td className="px-4 py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full ${t.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-600" : "bg-muted text-muted-foreground"}`}>{t.payment_status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

const Card = ({ label, value, testid }) => (
  <div data-testid={testid ? `billing-${testid}` : undefined} className="rounded-2xl border border-border bg-card p-4"><div className="text-xs text-muted-foreground">{label}</div><div className="text-2xl font-display font-extrabold mt-1">{value}</div></div>
);
